import AppSwitch from '@nxsys/components/AppSwitch';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { filter } from 'lodash';
import moment from 'moment';

export const getColumns = ({
  messages,
  handleViewCompany,
  handleToggleCompany,
  isBureau,
  isIreland,
}: {
  messages: TypedMessages;
  handleViewCompany: (company: ICompany) => void;
  handleToggleCompany: ({
    company,
    checked,
  }: {
    company: ICompany;
    checked: boolean;
  }) => void;
  isBureau?: boolean;
  isIreland?: boolean;
}): ColumnsType<ICompany> => {
  if (isBureau) {
    return [
      {
        key: 'action',
        title: messages['common.action'],
        fixed: 'left',
        width: 120,
        align: 'left',
        render: (record) => (
          <ul className="flex items-center justify-start gap-x-4">
            <li onClick={() => handleViewCompany(record)}>
              <Tooltip title="Edit">
                <span><Icons.IconEdit /></span>
              </Tooltip>
            </li>
            <li>
              <AppSwitch
                checked={record.isActive}
                onChange={(checked) =>
                  handleToggleCompany({ company: record, checked })
                }
              />
            </li>
          </ul>
        ),
      },
      {
        key: 'id',
        title: isBureau
          ? messages['common.employerId']
          : messages['common.companyId'],
        dataIndex: 'id',
        sorter: true,
        fixed: 'left',
      },
      {
        key: 'companyName',
        title: isBureau
          ? messages['common.bureau.companyName']
          : messages['common.companyName'],
        dataIndex: 'companyName',
        sorter: true,
        fixed: 'left',
        render: (value) => value || '-',
      },
      {
        key: 'status',
        title: messages['export.company.status'] ?? messages['common.status'],
        dataIndex: 'isActive',
        render: (value: boolean) => (value ? 'Active' : 'Inactive'),
      },
      {
        key: 'totalEmployees',
        title: messages['export.company.totalEmployees'],
        dataIndex: 'totalEmployees',
        render: (value) => ((value ?? value === 0) ? value : '-'),
      },
      {
        key: 'paymentFrequency',
        title:
          messages['export.company.payFrequency'] ??
          messages['common.payFrequency'],
        dataIndex: 'paymentFrequency',
        sorter: true,
        render: (value) =>
          value
            ? filter(defaultPaymentFrequency, (item) => item.value === value)[0]
                ?.label
            : '-',
      },
      {
        key: 'lastPeriod',
        title: messages['export.company.lastPeriod'],
        render: (record) => {
          const latestWeeklyPayroll = record?.latestWeeklyPayroll?.updatedAt
            ? moment(record?.latestWeeklyPayroll?.updatedAt).format(
                'DD/MM/YYYY',
              )
            : '';
          const latest2WeeklyPayroll = record?.latest2WeeklyPayroll?.updatedAt
            ? moment(record?.latest2WeeklyPayroll?.updatedAt).format(
                'DD/MM/YYYY',
              )
            : '';
          const latest4WeeklyPayroll = record?.latest4WeeklyPayroll?.updatedAt
            ? moment(record?.latest4WeeklyPayroll?.updatedAt).format(
                'DD/MM/YYYY',
              )
            : '';
          const latestMonthlyPayroll = record?.latestMonthlyPayroll?.updatedAt
            ? moment(record?.latestMonthlyPayroll?.updatedAt).format(
                'DD/MM/YYYY',
              )
            : '';
          return (
            <div>
              {latestWeeklyPayroll && <div>Weekly: {latestWeeklyPayroll}</div>}
              {latest2WeeklyPayroll && (
                <div>2 Weekly: {latest2WeeklyPayroll}</div>
              )}
              {latest4WeeklyPayroll && (
                <div>4 Weekly: {latest4WeeklyPayroll}</div>
              )}
              {latestMonthlyPayroll && (
                <div>Monthly: {latestMonthlyPayroll}</div>
              )}
            </div>
          );
        },
      },
    ];
  }

  return [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      width: 100,
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-4">
          <li onClick={() => handleViewCompany(record)}>
            <Tooltip title="Edit">
              <span><Icons.IconEdit /></span>
            </Tooltip>
          </li>
        </ul>
      ),
    },
    {
      key: 'id',
      title: messages['common.companyId'],
      dataIndex: 'id',
      sorter: true,
      fixed: 'left',
    },
    {
      key: 'companyName',
      title: isBureau ? messages['common.bureau.companyName'] : messages['common.companyName'],
      dataIndex: 'companyName',
      sorter: true,
      fixed: 'left',
      render: (value) => value || '-',
    },
    {
      key: 'companyNo',
      title: messages['common.companyNo'],
      dataIndex: 'companyNo',
      sorter: true,
      render: (value) => value || '-',
    },
    {
      key: 'email',
      title: messages['common.email'],
      dataIndex: 'email',
      render: (value) => value || '-',
    },
    {
      key: 'telephone',
      title: messages['common.phone'],
      dataIndex: 'telephone',
      render: (value) => value || '-',
    },
    {
      key: 'VATRegistered',
      title: messages['common.vatRegistered'],
      dataIndex: 'VATRegistered',
      sorter: true,
      render: (value) =>
        value ? (
          <Icons.IconCheckCircle />
        ) : (
          <Icons.IconCloseCircle color={defaultTheme.theme.palette.black} />
        ),
    },
    {
      key: 'UTRNo',
      title: isIreland ? 'TRN Number' : messages['common.utrNumber'],
      dataIndex: 'UTRNo',
      sorter: true,
      render: (value) => value || '-',
    },
  ];
};
